/**
 * Loads ``formatElementTypeDisplay.yaml``:
 * ``common`` (merged into every page) + ``page → category → id → { material_symbol, label }``.
 *
 * TypeScript exposes ``materialSymbol`` (camelCase); YAML uses ``material_symbol``.
 */

import { parse } from 'yaml';

import rawDisplayYaml from './formatElementTypeDisplay.yaml?raw';

/** Reserved top-level key merged into all pages (not a routable page id). */
export const PAGE_DISPLAY_COMMON_KEY = 'common';

export interface FormatElementTypeDisplay {
  /** Material Symbols ligature name (``material-symbols-outlined``). */
  materialSymbol: string;
  /** Human-readable label for ``title`` / ``aria-label``. */
  label: string;
}

/**
 * YAML / ``getIconDisplay`` page id for the creative editor (``src/pages/Creative.tsx``).
 * Must match a top-level key (other than ``common``) in ``formatElementTypeDisplay.yaml``.
 */
export const CREATIVE_EDITOR_PAGE = 'creative_editor';

/** Category key for ``FormatElementType`` / API ``element_type`` strings. */
export const ICON_CATEGORY_ELEMENT_TYPE = 'element_type';

/** Toolbar, render drawer, and footer chrome on ``Creative.tsx``. */
export const ICON_CATEGORY_CREATIVE_EDITOR_CHROME = 'chrome';

type PageCategoryMaps = Readonly<
  Record<string, Readonly<Record<string, FormatElementTypeDisplay>>>
>;

export interface PageDisplayIconConfig {
  /** Merged pages only; ``common`` is not included. */
  readonly pages: Readonly<Record<string, PageCategoryMaps>>;
}

function readMaterialSymbol(row: Record<string, unknown>): string | undefined {
  const snake = row.material_symbol;
  if (typeof snake === 'string') return snake;
  const legacy = row.materialSymbol;
  return typeof legacy === 'string' ? legacy : undefined;
}

function parseIconLeafMap(
  rawKey: string,
  value: unknown,
  context: string,
): Record<string, FormatElementTypeDisplay> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new Error(`${context}: "${rawKey}" must be a mapping of id → icon row`);
  }
  const out: Record<string, FormatElementTypeDisplay> = {};
  for (const [idRaw, rowVal] of Object.entries(value as Record<string, unknown>)) {
    const id = idRaw.trim().toLowerCase();
    if (!id) continue;
    if (typeof rowVal !== 'object' || rowVal === null || Array.isArray(rowVal)) {
      throw new Error(`${context}.${idRaw}: must be an object`);
    }
    const row = rowVal as Record<string, unknown>;
    const materialSymbol = readMaterialSymbol(row);
    const label = row.label;
    if (typeof materialSymbol !== 'string' || typeof label !== 'string') {
      throw new Error(
        `${context}.${idRaw}: needs string material_symbol (or legacy materialSymbol) and label`,
      );
    }
    out[id] = { materialSymbol, label };
  }
  return out;
}

function parsePageCategories(pageKey: string, pageVal: unknown): PageCategoryMaps {
  if (typeof pageVal !== 'object' || pageVal === null || Array.isArray(pageVal)) {
    throw new Error(`formatElementTypeDisplay.yaml: page "${pageKey}" must be a mapping`);
  }
  const categories: Record<string, Readonly<Record<string, FormatElementTypeDisplay>>> = {};
  for (const [catRaw, catVal] of Object.entries(pageVal as Record<string, unknown>)) {
    const catKey = catRaw.trim();
    if (!catKey) continue;
    categories[catKey] = parseIconLeafMap(catRaw, catVal, `page "${pageKey}" category "${catRaw}"`);
  }
  return categories;
}

function mergePageCategories(base: PageCategoryMaps, override: PageCategoryMaps): PageCategoryMaps {
  const catKeys = new Set([...Object.keys(base), ...Object.keys(override)]);
  const out: Record<string, Readonly<Record<string, FormatElementTypeDisplay>>> = {};
  for (const cat of catKeys) {
    out[cat] = { ...(base[cat] ?? {}), ...(override[cat] ?? {}) };
  }
  return out;
}

function parsePageDisplayYaml(source: string): PageDisplayIconConfig {
  const parsed: unknown = parse(source);
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error(
      'formatElementTypeDisplay.yaml: root must be a mapping (common + page_name → categories)',
    );
  }
  const root = parsed as Record<string, unknown>;
  const commonKey = PAGE_DISPLAY_COMMON_KEY.toLowerCase();
  const commonVal = root[PAGE_DISPLAY_COMMON_KEY] ?? root[commonKey];
  const commonCategories: PageCategoryMaps =
    commonVal !== undefined ? parsePageCategories(PAGE_DISPLAY_COMMON_KEY, commonVal) : {};

  const pages: Record<string, PageCategoryMaps> = {};
  for (const [pageRaw, pageVal] of Object.entries(root)) {
    const pageKey = pageRaw.trim().toLowerCase();
    if (!pageKey || pageKey === commonKey) continue;
    const pageCategories = parsePageCategories(pageRaw, pageVal);
    pages[pageKey] = mergePageCategories(commonCategories, pageCategories);
  }

  return { pages: Object.freeze(pages) };
}

const CONFIG: PageDisplayIconConfig = parsePageDisplayYaml(rawDisplayYaml);

const FALLBACK: FormatElementTypeDisplay = {
  materialSymbol: 'widgets',
  label: 'Item',
};

/**
 * Resolved icon row for ``pageName`` / ``category`` / ``itemKey``, or a generic fallback.
 * ``common`` chrome (and other categories) is merged into each page; page rows override.
 */
export function getIconDisplay(
  pageName: string,
  category: string,
  itemKey: string,
): FormatElementTypeDisplay {
  const page = pageName.trim().toLowerCase();
  const cat = category.trim();
  const key = itemKey.trim().toLowerCase();
  const hit = CONFIG.pages[page]?.[cat]?.[key];
  if (hit) return hit;
  return {
    ...FALLBACK,
    label: itemKey.trim() || FALLBACK.label,
  };
}

/** Merged pages only; ``common`` is folded in — not a key here. */
export const PAGE_DISPLAY_ICON_CONFIG: PageDisplayIconConfig = CONFIG;

/** Map for ``creative_editor`` → ``element_type`` (convenience for imports that need the table). */
export const FORMAT_ELEMENT_TYPE_DISPLAY: Readonly<Record<string, FormatElementTypeDisplay>> =
  Object.freeze({
    ...(CONFIG.pages[CREATIVE_EDITOR_PAGE]?.[ICON_CATEGORY_ELEMENT_TYPE] ?? {}),
  });

export function getFormatElementTypeDisplay(elementType: string): FormatElementTypeDisplay {
  return getIconDisplay(CREATIVE_EDITOR_PAGE, ICON_CATEGORY_ELEMENT_TYPE, elementType);
}

/** Icon + label for creative editor page chrome (toolbar, drawer, footer). */
export function getCreativeEditorChromeIcon(id: string): FormatElementTypeDisplay {
  return getIconDisplay(CREATIVE_EDITOR_PAGE, ICON_CATEGORY_CREATIVE_EDITOR_CHROME, id);
}
